import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from './Sidebar';
import ChatArea from './ChatArea';
import VideoCall from './VideoCall';

const Dashboard = () => {
  const [currentUser, setCurrentUser] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [activeConversation, setActiveConversation] = useState(null);
  const [messages, setMessages] = useState({});
  const [isInCall, setIsInCall] = useState(false);
  const [isIncomingCall, setIsIncomingCall] = useState(false);
  const [callData, setCallData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();
  
  // Load user data and conversations on mount
  useEffect(() => {
    // Check if user is logged in
    const userData = localStorage.getItem('czoneUser');
    
    if (!userData) {
      // Redirect to login if no user data
      navigate('/');
      return;
    }
    
    try {
      const user = JSON.parse(userData);
      setCurrentUser(user);
      
      // Load conversations (in a real app, this would come from an API)
      loadConversations(user.id);
    } catch (error) {
      console.error('Error loading user data:', error);
      navigate('/');
    }
  }, [navigate]);
  
  // Mock function to load conversations
  const loadConversations = (userId) => {
    // In a real app, this would be an API call
    setTimeout(() => {
      const mockConversations = [
        {
          id: 'conv1',
          name: 'Alice Johnson',
          avatar: null,
          lastMessage: 'When is the meeting scheduled?',
          lastMessageType: 'text',
          lastMessageTime: '10:30 AM',
          isOnline: true,
          unreadCount: 2
        },
        {
          id: 'conv2',
          name: 'Bob Smith',
          avatar: null,
          lastMessage: 'Check out this document',
          lastMessageType: 'file',
          lastMessageTime: 'Yesterday',
          isOnline: false,
          unreadCount: 0
        },
        {
          id: 'conv3',
          name: 'CZone Team',
          avatar: null,
          lastMessage: 'Welcome to CZone!',
          lastMessageType: 'text',
          lastMessageTime: '07/05',
          isOnline: true,
          unreadCount: 0
        },
        {
          id: 'group1',
          name: 'Project Alpha Team',
          avatar: null,
          lastMessage: 'Meeting notes attached',
          lastMessageType: 'file',
          lastMessageTime: '07/06',
          isOnline: true,
          isGroup: true,
          unreadCount: 5
        }
      ];
      
      setConversations(mockConversations);
      
      // Load mock messages for each conversation
      const mockMessages = {};
      mockConversations.forEach(conv => {
        mockMessages[conv.id] = generateMockMessages(conv.id, userId);
      });
      
      setMessages(mockMessages);
      setIsLoading(false);
    }, 1000);
  };
  
  // Generate mock messages for a conversation
  const generateMockMessages = (conversationId, userId) => {
    const otherUserId = `other-${conversationId}`;
    const otherUserName = conversations.find(c => c.id === conversationId)?.name || 'User';
    
    // In a real app, messages would come from an API
    const mockMessages = [
      {
        id: `msg1-${conversationId}`,
        senderId: otherUserId,
        senderName: otherUserName,
        type: 'text',
        content: 'Hello there!',
        timestamp: new Date(Date.now() - 3600000).toISOString() // 1 hour ago
      },
      {
        id: `msg2-${conversationId}`,
        senderId: userId,
        senderName: 'You',
        type: 'text',
        content: 'Hi! How are you?',
        timestamp: new Date(Date.now() - 3500000).toISOString() // 58 minutes ago
      },
      {
        id: `msg3-${conversationId}`,
        senderId: otherUserId,
        senderName: otherUserName,
        type: 'text',
        content: 'I\'m doing well, thanks for asking!',
        timestamp: new Date(Date.now() - 3400000).toISOString() // 56 minutes ago
      }
    ];
    
    // Add some conversation-specific messages
    if (conversationId === 'conv1') {
      mockMessages.push({
        id: 'msg-alice-1',
        senderId: otherUserId,
        senderName: otherUserName,
        type: 'text',
        content: 'When is the meeting scheduled?',
        timestamp: new Date(Date.now() - 1800000).toISOString() // 30 minutes ago
      });
    } else if (conversationId === 'conv2') {
      mockMessages.push({
        id: 'msg-bob-1',
        senderId: otherUserId,
        senderName: otherUserName,
        type: 'file',
        fileName: 'project_report.pdf',
        fileSize: 2500000,
        content: '#',
        timestamp: new Date(Date.now() - 86400000).toISOString() // 1 day ago
      });
    } else if (conversationId === 'conv3') {
      mockMessages.push({
        id: 'msg-czone-1',
        senderId: otherUserId,
        senderName: otherUserName,
        type: 'text',
        content: 'Welcome to CZone! We\'re excited to have you here.',
        timestamp: new Date(Date.now() - 172800000).toISOString() // 2 days ago
      });
    }
    
    return mockMessages;
  };
  
  // Handle sending a new message
  const handleSendMessage = (message) => {
    if (!activeConversation) return;
    
    const newMessage = {
      id: `msg-${Date.now()}`,
      senderId: currentUser.id,
      senderName: currentUser.name,
      ...message
    };
    
    // Add message to the conversation
    setMessages(prev => ({
      ...prev,
      [activeConversation.id]: [
        ...(prev[activeConversation.id] || []),
        newMessage
      ]
    }));
    
    // Update conversation's last message
    setConversations(prev => 
      prev.map(conv => 
        conv.id === activeConversation.id
          ? {
              ...conv,
              lastMessage: message.type === 'text' 
                ? message.content 
                : `Sent a ${message.type}`,
              lastMessageType: message.type,
              lastMessageTime: 'Just now'
            }
          : conv
      )
    );
    
    // In a real app, send the message to the server
    console.log('Sending message:', newMessage);
  };
  
  // Handle starting a new conversation
  const handleNewConversation = () => {
    // In a real app, this would open a modal to select users
    const newId = `new-${Date.now()}`;
    const newConversation = {
      id: newId,
      name: 'New Conversation',
      avatar: null,
      lastMessage: '',
      lastMessageTime: 'New',
      isOnline: false,
      unreadCount: 0
    };
    
    setConversations(prev => [newConversation, ...prev]);
    setActiveConversation(newConversation);
    setMessages(prev => ({
      ...prev,
      [newId]: []
    }));
  };
  
  // Handle starting a video call
  const handleStartVideoCall = () => {
    if (!activeConversation) return;
    
    setCallData({
      conversation: activeConversation,
      roomId: `czone-${activeConversation.id}-${Date.now()}`
    });
    
    setIsInCall(true);
  };
  
  // Handle ending a video call
  const handleEndCall = () => {
    setIsInCall(false);
    setIsIncomingCall(false);
    setCallData(null);
  };
  
  // If still loading
  if (isLoading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <img src="/cdezone_logo.jpg" alt="CZone Logo" className="czone-logo" />
        <p>Loading CZone...</p>
      </div>
    );
  }
  
  // If in a call, show the video call component
  if (isInCall) {
    return (
      <VideoCall
        conversation={callData.conversation}
        currentUser={currentUser}
        onEndCall={handleEndCall}
        isIncoming={isIncomingCall}
        roomId={callData.roomId}
      />
    );
  }

  return (
    <div className="dashboard">
      <Sidebar
        currentUser={currentUser}
        conversations={conversations}
        activeConversation={activeConversation}
        setActiveConversation={setActiveConversation}
        onNewConversation={handleNewConversation}
      />
      
      <ChatArea
        conversation={activeConversation}
        currentUser={currentUser}
        onSendMessage={handleSendMessage}
        onStartVideoCall={handleStartVideoCall}
        messages={activeConversation ? messages[activeConversation.id] : []}
      />
    </div>
  );
};

export default Dashboard;
